import { useEffect, useMemo, useRef } from "react";

import { Result, getReadableErrorMessage } from "@bridgesplit/utils";
import { useWallets } from "@privy-io/react-auth/solana";
import { useDispatch } from "react-redux";

import { updateStateWalletAddress, useAuthStorage, useResetAllWalletData } from "../reducers";
import { TransactionWalletAuth } from "../types";
import { useLoopscaleWalletContext, getDebugWallet } from "../wallet";
import { getPrivyWalletMatch, normalizePrivyWalletClientType } from "./privyHelpers";

export function useTransactionWalletAuth() {
    const { getWeb3UserResult } = useLoopscaleWalletContext();

    return function (): Result<TransactionWalletAuth> {
        try {
            const loopscaleWallet = getWeb3UserResult().unwrap();
            if (!loopscaleWallet) return Result.errFromMessage("You haven't registered your wallet");
            return Result.ok({ wallet: loopscaleWallet });
        } catch (error) {
            // catches browser wallet errors
            return Result.errWithDebug(getReadableErrorMessage("access your connected wallet"), error);
        }
    };
}

export function useWalletDisconnect() {
    const { resetCookies } = useAuthStorage();
    const resetAllWalletData = useResetAllWalletData();
    const dispatch = useDispatch();

    function disconnectWallet() {
        resetCookies();
        dispatch(updateStateWalletAddress({ address: undefined, walletClientType: undefined }));
        resetAllWalletData();
    }

    return { disconnectWallet };
}

/**
 * Syncs Privy wallet address with Redux auth state and handles external wallet switches.
 */
export function useSyncLoopscaleWallet() {
    const { wallets: privyWallets, ready } = useWallets();

    const dispatch = useDispatch();
    const { state, setWalletCookie, getWalletCookieAddress, getWalletCookieClientType } = useAuthStorage();
    const resetAllWalletData = useResetAllWalletData();
    const privyWallet = privyWallets[0];

    const privyWalletAddress = privyWallet?.address;
    const privyWalletClientType = normalizePrivyWalletClientType(privyWallet?.standardWallet.name);

    const stateClientType = state.walletClientType;
    const stateWalletAddress = state.stateWalletAddress;

    const walletCookieAddress = getWalletCookieAddress();
    const walletCookieClientType = getWalletCookieClientType();
    const hasInitializedRef = useRef(false);
    const isMobileBrowser = useMemo(() => isMobile(), []);

    const hasValidWalletCookie = walletCookieAddress && walletCookieClientType;

    useEffect(() => {
        if (!ready) return;
        if (stateWalletAddress) {
            hasInitializedRef.current = true;
            return;
        }

        if (hasInitializedRef.current) return;

        const debugWallet = getDebugWallet();
        if (debugWallet) {
            dispatch(updateStateWalletAddress({ address: debugWallet, walletClientType: undefined }));
            hasInitializedRef.current = true;
            return;
        }

        if (isMobileBrowser && privyWallets && privyWallets.length > 0) {
            dispatch(
                updateStateWalletAddress({ address: privyWalletAddress, walletClientType: privyWalletClientType })
            );
            hasInitializedRef.current = true;
            return;
        }

        // Skip if state already initialized or no cookie
        if (!hasValidWalletCookie) {
            hasInitializedRef.current = true;
            return;
        }

        // Sync from cookie on mount
        if (privyWallets && privyWallets.length > 0) {
            const privyMatchingCookieWallet = getPrivyWalletMatch(
                privyWallets,
                walletCookieAddress,
                walletCookieClientType
            );
            if (privyMatchingCookieWallet) {
                dispatch(
                    updateStateWalletAddress({
                        address: privyMatchingCookieWallet.address,
                        walletClientType: privyMatchingCookieWallet.standardWallet.name
                    })
                );
                resetAllWalletData();
                setWalletCookie(privyMatchingCookieWallet.address, privyMatchingCookieWallet.standardWallet.name);
            }
        }
        hasInitializedRef.current = true;
    }, [
        stateWalletAddress,
        hasValidWalletCookie,
        dispatch,
        setWalletCookie,
        ready,
        privyWallets,
        isMobileBrowser,
        privyWalletAddress,
        privyWalletClientType,
        walletCookieAddress,
        walletCookieClientType,
        resetAllWalletData
    ]);

    const previousPrivyWalletAddressRef = useRef<string | undefined>(stateWalletAddress);

    // Ongoing sync: Safely listens for wallet changes and syncs state
    // Handles external wallet switches and stale wallet cleanup
    useEffect(() => {
        if (!ready || getDebugWallet()) return;
        const previousPrivyWalletAddress = previousPrivyWalletAddressRef.current;

        // Case 1: External wallet switch - user changed active wallet in browser extension
        if (
            stateWalletAddress &&
            privyWalletAddress &&
            privyWalletAddress !== previousPrivyWalletAddress &&
            privyWalletAddress !== stateWalletAddress &&
            privyWalletClientType === stateClientType
        ) {
            dispatch(
                updateStateWalletAddress({ address: privyWalletAddress, walletClientType: privyWalletClientType })
            );
            setWalletCookie(privyWalletAddress, privyWalletClientType);

            resetAllWalletData();
        } else if (
            // Case 2: Stale wallet - Redux has a wallet that's no longer in Privy's array
            // This happens when Privy doesn't update privyWallets[0] reliably
            // Clear Redux to null - don't try to guess which wallet to sync to
            stateWalletAddress &&
            !getPrivyWalletMatch(privyWallets, stateWalletAddress, stateClientType)
        ) {
            dispatch(updateStateWalletAddress({ address: undefined, walletClientType: undefined }));

            resetAllWalletData();
        }

        previousPrivyWalletAddressRef.current = privyWalletAddress;
    }, [
        dispatch,
        privyWalletAddress,
        stateWalletAddress,
        resetAllWalletData,
        setWalletCookie,
        hasValidWalletCookie,
        privyWallets,
        ready,
        privyWalletClientType,
        stateClientType
    ]);
}

/**
 * Detects if the user is in a mobile in-app browser/webview (e.g., Phantom mobile app).
 * This is used to auto-connect to wallets[0] in these environments.
 *
 * Returns true for: Mobile wallet app webviews
 * Returns false for: Desktop browsers, mobile Safari, mobile Chrome/Brave with extensions
 *
 * Logic:
 * - Must be on a mobile device (Android/iOS)
 * - Exclude Safari (real mobile browser, not in-app)
 * - Exclude browsers with extension APIs (Chrome/Brave are real browsers)
 * - What remains: in-app webviews without extension support
 *
 * Note: This is a heuristic and may not be 100% accurate for all wallet apps.
 */
export function isMobile(): boolean {
    if (typeof window === "undefined") return false;

    const ua = navigator.userAgent;

    // Must be on a mobile device
    const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(ua);
    if (!isMobileDevice) return false;

    // Safari is a real mobile browser, not an in-app webview
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|OPR|EdgiOS/i.test(ua);
    if (isSafari) return false;

    // Real mobile browsers with extensions (Android Chrome/Brave)
    const hasExtensions = !!((window as any).chrome?.runtime || (window as any).browser?.runtime);

    // Mobile + not Safari + no extensions → in-app webview
    return !hasExtensions;
}
